// src/app/shared/services/form-draft.service.ts
import { Injectable, inject } from '@angular/core';
import { from, map, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AppApis } from "../../core/APIs/app-apis";
import { CrudApiService, GenResp } from "@template-ui/services/crud-api.service";

/**
 * Wire format used when persisting drafts. The API expects stringified JSON payloads
 * keyed by `draftKey` and scoped to a particular owner (`partId`).
 */
export interface DraftEnvelope<T = unknown> {
  draftKey: string;
  partId: string;
  payload: T;
  updatedUtc?: string;
}

/**
 * Client facade around the draft REST endpoints. Handles hashing to avoid redundant
 * writes, upsert semantics for POST/PUT, and JSON (de)serialisation so callers always
 * interact with plain objects.
 */
@Injectable({ providedIn: 'root' })
export class EZFormDraftService {
  private readonly crudService = inject(CrudApiService);
  private readonly hashes = new Map<string, string>();
  private readonly existingKeys = new Set<string>();
  bitsApi = inject(AppApis);

  /**
   * Fetches the current draft (if any) for the provided key. Maintains bookkeeping so
   * subsequent saves know whether to POST or PUT, and keeps a hash of the last payload
   * to short-circuit duplicate saves.
   */
  load<T = unknown>(key: string) {
    const url = this.bitsApi.core.getFormDraftByDraftKey(key);
    return this.crudService.getbyUrl<DraftEnvelope<T>>(url).pipe(
      map(resp => this.deserializeEnvelope<T>(this.unwrapResponse<DraftEnvelope<T>>(resp))),
      switchMap(draft => {
        if (!draft) {
          this.existingKeys.delete(key);
          this.hashes.delete(key);
          return of(undefined);
        }
        this.existingKeys.add(key);
        const serialized = JSON.stringify(draft.payload ?? {});
        return from(this.hash(serialized)).pipe(
          tap(hash => this.hashes.set(key, hash)),
          map(() => draft)
        );
      }),
      catchError(err => {
        if (err.status === 404) {
          this.existingKeys.delete(key);
          this.hashes.delete(key);
          return of(undefined);
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Upserts the draft entry. If the payload hasn't changed since the last save, we skip
   * the API call entirely.
   */
  save<T = unknown>(envelope: DraftEnvelope<T>) {
    const serialized = JSON.stringify(envelope.payload ?? {});
    return from(this.hash(serialized)).pipe(
      switchMap(hash => {
        if (this.hashes.get(envelope.draftKey) === hash) return of(void 0); // nothing changed
        const persist$ = this.existingKeys.has(envelope.draftKey)
          ? this.putDraft(envelope)
          : this.postDraft(envelope).pipe(
              catchError(err => {
                if (err.status === 409 || err.status === 405) {
                  this.existingKeys.add(envelope.draftKey);
                  return this.putDraft(envelope);
                }
                return throwError(() => err);
              })
            );

        return persist$.pipe(
          tap(() => {
            this.existingKeys.add(envelope.draftKey);
            this.hashes.set(envelope.draftKey, hash);
          }),
          map(() => void 0)
        );
      })
    );
  }

  /**
   * Removes the stored draft once the authoritative save succeeds.
   */
  clear(key: string) {
    this.hashes.delete(key);
    this.existingKeys.delete(key);
    const url = this.bitsApi.core.deleteFormDraftByDraftKey(key);
    return this.crudService.deleteByUrl<void>(url).pipe(
      map(() => void 0),
      catchError(err => (err.status === 404 ? of(void 0) : throwError(() => err)))
    );
  }

  private async hash(value: string): Promise<string> {
    if (crypto?.subtle) {
      const data = new TextEncoder().encode(value);
      const digest = await crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
    }
    let hash = 2166136261;
    for (let i = 0; i < value.length; i++) hash = (hash ^ value.charCodeAt(i)) * 16777619;
    return (hash >>> 0).toString(16);
  }

  private unwrapResponse<T>(resp: GenResp<T> | T | undefined | null): T | undefined {
    if (!resp) {
      return undefined;
    }

    if ((resp as GenResp<T>).data !== undefined) {
      const genResp = resp as GenResp<T>;
      return genResp.data ?? undefined;
    }

    return resp as T;
  }

  /**
   * Issues a POST for a new draft entry.
   */
  private postDraft<T>(envelope: DraftEnvelope<T>) {
    const url = this.bitsApi.core.postFormDraft();
    const body = this.serializeEnvelope(envelope);
    return this.crudService.postByUrl<DraftEnvelope<T>>(url, body).pipe(
      map(resp => this.deserializeEnvelope<T>(this.unwrapResponse<DraftEnvelope<T>>(resp))),
      tap(draft => {
        this.existingKeys.add((draft?.draftKey ?? envelope.draftKey));
      })
    );
  }

  /**
   * Issues a PUT for an existing draft entry.
   */
  private putDraft<T>(envelope: DraftEnvelope<T>) {
    const url = this.bitsApi.core.putFormDraftByDraftKey(envelope.draftKey);
    const body = this.serializeEnvelope(envelope);
    return this.crudService.putByUrl<DraftEnvelope<T>>(url, body).pipe(
      map(resp => this.deserializeEnvelope<T>(this.unwrapResponse<DraftEnvelope<T>>(resp))),
      tap(draft => {
        this.existingKeys.add((draft?.draftKey ?? envelope.draftKey));
      })
    );
  }

  /**
   * Ensures payloads are always JSON strings before sending them to the API.
   */
  private serializeEnvelope<T>(envelope: DraftEnvelope<T>): DraftEnvelope<string> {
    return {
      ...envelope,
      payload: JSON.stringify(envelope.payload ?? {})
    };
  }

  /**
   * Normalises API responses so callers always receive objects (never raw JSON strings).
   */
  private deserializeEnvelope<T>(envelope?: DraftEnvelope<any> | null): DraftEnvelope<T> | undefined {
    if (!envelope) {
      return undefined;
    }

    let payload: any = envelope.payload;
    if (typeof payload === 'string') {
      try {
        payload = payload ? JSON.parse(payload) : {};
      } catch (err) {
        console.error('[DynamicFormDraftService] Failed to parse draft payload', err);
        payload = {};
      }
    }

    return { ...(envelope as DraftEnvelope<any>), payload } as DraftEnvelope<T>;
  }
}
